import { useState, useEffect } from 'react'
import { api } from '../lib/api'
import { useToast } from '../components/Toast'
import {
  Link2,
  Check,
  X,
  AlertTriangle,
  Sparkles,
  RefreshCw,
  Loader2,
  Info,
} from 'lucide-react'

interface ControlMapping {
  id: string
  source_control_id: string
  target_control_id: string
  source_control_number: string
  source_control_name: string
  source_framework_name: string
  target_control_number: string
  target_control_name: string
  target_framework_name: string
  ai_confidence: number
  ai_reasoning: string
  mapping_type: 'equivalent' | 'partial' | 'related'
  status: 'suggested' | 'approved' | 'rejected' | 'pending_review'
  reviewed_by_username?: string
  reviewed_at?: number
  created_at: number
}

interface Framework {
  id: string
  name: string
  display_name: string
  enabled: boolean
}

interface MappingStats {
  total_mappings: number
  suggested: number
  approved: number
  rejected: number
  avg_confidence: number
  evidence_links: number
}

const mappingTypeConfig = {
  equivalent: { label: 'Equivalent', color: 'bg-green-100 text-green-800' },
  partial: { label: 'Partial', color: 'bg-yellow-100 text-yellow-800' },
  related: { label: 'Related', color: 'bg-blue-100 text-blue-800' },
}

const statusConfig = {
  suggested: { label: 'Suggested', color: 'bg-purple-100 text-purple-800', icon: Sparkles },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-800', icon: Check },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: X },
  pending_review: { label: 'Pending Review', color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
}

export default function ControlMappings() {
  const toast = useToast()
  const [mappings, setMappings] = useState<ControlMapping[]>([])
  const [frameworks, setFrameworks] = useState<Framework[]>([])
  const [stats, setStats] = useState<MappingStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [analyzing, setAnalyzing] = useState(false)
  const [statusFilter, setStatusFilter] = useState('suggested')
  const [frameworkFilter, setFrameworkFilter] = useState('')
  const [showAnalyzeModal, setShowAnalyzeModal] = useState(false)
  const [analyzeSourceFramework, setAnalyzeSourceFramework] = useState('')
  const [analyzeTargetFramework, setAnalyzeTargetFramework] = useState('')

  useEffect(() => {
    loadMappings()
    loadFrameworks()
    loadStats()
  }, [statusFilter, frameworkFilter])

  async function loadMappings() {
    setLoading(true)
    try {
      const response = await api.getMappings({
        status: statusFilter || undefined,
        framework_id: frameworkFilter || undefined,
      })
      if (response.success) {
        setMappings(response.mappings)
      }
    } catch (err) {
      console.error('Failed to load mappings:', err)
    } finally {
      setLoading(false)
    }
  }

  async function loadFrameworks() {
    try {
      const response = await api.getFrameworks()
      if (response.success) {
        setFrameworks(response.frameworks.filter((f: Framework) => f.enabled))
      }
    } catch (err) {
      console.error('Failed to load frameworks:', err)
    }
  }

  async function loadStats() {
    try {
      const response = await api.getMappingStats()
      if (response.success) {
        setStats(response.stats)
      }
    } catch (err) {
      console.error('Failed to load stats:', err)
    }
  }

  async function runAnalysis() {
    if (!analyzeSourceFramework) {
      toast.warning('Please select a source framework')
      return
    }

    setAnalyzing(true)
    setShowAnalyzeModal(false)
    try {
      const response = await api.analyzeMappings({
        source_framework_id: analyzeSourceFramework,
        target_framework_id: analyzeTargetFramework || undefined,
        min_confidence: 0.7,
      })
      if (response.success) {
        toast.success(`Analysis complete! Created ${response.suggestions_count} new mapping suggestions.`)
        loadMappings()
        loadStats()
      }
    } catch (err) {
      console.error('Analysis failed:', err)
      toast.error('Analysis failed. Please try again.')
    } finally {
      setAnalyzing(false)
    }
  }

  async function handleApprove(id: string) {
    try {
      await api.updateMapping(id, { status: 'approved' })
      toast.success('Mapping approved')
      loadMappings()
      loadStats()
    } catch (err) {
      console.error('Failed to approve:', err)
      toast.error('Failed to approve mapping')
    }
  }

  async function handleReject(id: string) {
    try {
      await api.updateMapping(id, { status: 'rejected' })
      toast.success('Mapping rejected')
      loadMappings()
      loadStats()
    } catch (err) {
      console.error('Failed to reject:', err)
      toast.error('Failed to reject mapping')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Control Mappings</h1>
          <p className="text-gray-500 mt-1">
            Map equivalent controls across frameworks to share evidence
          </p>
        </div>
        <button
          onClick={() => setShowAnalyzeModal(true)}
          disabled={analyzing}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {analyzing ? (
            <RefreshCw className="h-5 w-5 animate-spin" />
          ) : (
            <Sparkles className="h-5 w-5" />
          )}
          {analyzing ? 'Analyzing...' : 'AI Analyze Controls'}
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-500">Total Mappings</div>
            <div className="text-2xl font-bold text-gray-900">{stats.total_mappings}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-500">Pending Review</div>
            <div className="text-2xl font-bold text-purple-600">{stats.suggested}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-500">Approved</div>
            <div className="text-2xl font-bold text-green-600">{stats.approved}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="text-sm text-gray-500">Evidence Links</div>
            <div className="text-2xl font-bold text-blue-600">{stats.evidence_links}</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Statuses</option>
              <option value="suggested">Suggested (AI)</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="pending_review">Pending Review</option>
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Framework</label>
            <select
              value={frameworkFilter}
              onChange={(e) => setFrameworkFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Frameworks</option>
              {frameworks.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.display_name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Mappings List */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : mappings.length === 0 ? (
          <div className="text-center py-12">
            <Link2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No mappings found</p>
            <p className="text-sm text-gray-400 mt-2">
              Click "AI Analyze Controls" to generate mapping suggestions
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source Control
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Target Control
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Confidence
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mappings.map((mapping) => {
                  const typeConfig = mappingTypeConfig[mapping.mapping_type] || mappingTypeConfig.related
                  const status = statusConfig[mapping.status] || statusConfig.suggested
                  const StatusIcon = status.icon

                  return (
                    <tr key={mapping.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <span className="text-xs text-gray-500">
                            {mapping.source_framework_name}
                          </span>
                          <div className="font-medium text-gray-900">
                            {mapping.source_control_number}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {mapping.source_control_name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <span className="text-xs text-gray-500">
                            {mapping.target_framework_name}
                          </span>
                          <div className="font-medium text-gray-900">
                            {mapping.target_control_number}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {mapping.target_control_name}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className="bg-primary-600 h-2 rounded-full"
                              style={{ width: `${mapping.ai_confidence * 100}%` }}
                            />
                          </div>
                          <span className="text-sm text-gray-600">
                            {Math.round(mapping.ai_confidence * 100)}%
                          </span>
                        </div>
                        {mapping.ai_reasoning && (
                          <p
                            className="text-xs text-gray-400 mt-1 truncate max-w-xs"
                            title={mapping.ai_reasoning}
                          >
                            {mapping.ai_reasoning}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${typeConfig.color}`}
                        >
                          {typeConfig.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${status.color}`}
                        >
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {status.label}
                        </span>
                        {mapping.reviewed_by_username && (
                          <p className="text-xs text-gray-400 mt-1">
                            by {mapping.reviewed_by_username}
                          </p>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {mapping.status === 'suggested' && (
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleApprove(mapping.id)}
                              className="p-1.5 text-green-600 hover:bg-green-100 rounded"
                              title="Approve"
                            >
                              <Check className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleReject(mapping.id)}
                              className="p-1.5 text-red-600 hover:bg-red-100 rounded"
                              title="Reject"
                            >
                              <X className="h-5 w-5" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">How Control Mappings Work</p>
          <p className="mt-1">
            Control mappings identify equivalent or related controls across different compliance
            frameworks. When you upload evidence for one control, the system will suggest linking
            it to mapped controls, reducing duplicate work and ensuring consistent compliance
            coverage.
          </p>
        </div>
      </div>

      {/* Analyze Modal */}
      {showAnalyzeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">AI Control Analysis</h2>
            <p className="text-gray-500 text-sm mb-4">
              Select frameworks to analyze. The AI will compare controls and suggest mappings
              between equivalent requirements.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Source Framework *
                </label>
                <select
                  value={analyzeSourceFramework}
                  onChange={(e) => setAnalyzeSourceFramework(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Select framework to analyze</option>
                  {frameworks.map((f) => (
                    <option key={f.id} value={f.id}>
                      {f.display_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Framework (optional)
                </label>
                <select
                  value={analyzeTargetFramework}
                  onChange={(e) => setAnalyzeTargetFramework(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Compare against all other frameworks</option>
                  {frameworks
                    .filter((f) => f.id !== analyzeSourceFramework)
                    .map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.display_name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowAnalyzeModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={runAnalysis}
                disabled={!analyzeSourceFramework}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                <Sparkles className="h-4 w-4 inline mr-2" />
                Start Analysis
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
